import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PostCard from '../components/PostCard';
import CommentDrawer from '../components/CommentDrawer';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { UserPlus, UserCheck, MessageSquare, Camera, Edit3, Check, X, HandCoins } from 'lucide-react';
import { motion } from 'framer-motion';

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=FFB000&color=000&bold=true&size=256`;

export const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, refreshUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [tab, setTab] = useState('posts');
  const [editing, setEditing] = useState(false);
  const [bio, setBio] = useState('');
  const [name, setName] = useState('');
  const [activeComment, setActiveComment] = useState(null);
  const [tips, setTips] = useState(null);
  const avatarInputRef = useRef(null);

  const isOwn = currentUser && profile && currentUser.user_id === profile.user_id;

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${userId}`);
      setProfile(data);
      setBio(data.bio || '');
      setName(data.name || '');
      const p = await api.get(`/posts/user/${userId}`);
      setPosts(p.data);
      if (currentUser && currentUser.user_id === userId) {
        const s = await api.get('/posts/saved');
        setSavedPosts(s.data);
        try {
          const t = await api.get('/tips/received');
          setTips(t.data);
        } catch {}
      }
    } catch {
      toast.error('Could not load profile');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const handleFollow = async () => {
    try {
      const { data } = await api.post(`/users/${userId}/follow`);
      setProfile(p => ({
        ...p,
        is_following: data.following,
        followers_count: p.followers_count + (data.following ? 1 : -1),
      }));
    } catch {}
  };

  const handleSave = async () => {
    try {
      await api.put('/users/me', { name, bio });
      toast.success('Profile updated');
      setEditing(false);
      refreshUser();
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/upload/image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await api.put('/users/me', { avatar_path: data.path });
      toast.success('Avatar updated');
      refreshUser();
      load();
    } catch {
      toast.error('Avatar upload failed');
    }
  };

  if (!profile) {
    return <Layout><div className="animate-pulse h-96 post-card" /></Layout>;
  }

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Header */}
        <div className="post-card p-6 md:p-8 mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="relative">
              <img
                src={profile.avatar_path ? fileUrl(profile.avatar_path) : avatarFallback(profile.name)}
                alt=""
                className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-2 border-[#FFB000]/30"
                data-testid="profile-avatar"
              />
              {isOwn && (
                <>
                  <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} data-testid="profile-avatar-input" />
                  <button
                    onClick={() => avatarInputRef.current?.click()}
                    className="absolute bottom-0 right-0 w-10 h-10 rounded-full bg-[#FFB000] text-black flex items-center justify-center shadow-lg hover:bg-[#E69E00]"
                    data-testid="profile-avatar-change"
                  >
                    <Camera size={16} />
                  </button>
                </>
              )}
            </div>

            <div className="flex-1 min-w-0">
              {editing ? (
                <div className="space-y-3 mb-4">
                  <input
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="input-default"
                    placeholder="Your name"
                    data-testid="profile-edit-name"
                  />
                  <textarea
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    rows={2}
                    className="input-default resize-none"
                    placeholder="Bio - tell the crew about your sound"
                    data-testid="profile-edit-bio"
                  />
                </div>
              ) : (
                <>
                  <h1 className="font-heading font-black text-white text-3xl md:text-4xl tracking-tighter" data-testid="profile-name">
                    {profile.name}
                  </h1>
                  <div className="text-[#A1A1AA] text-sm mb-3">@{profile.username}</div>
                  {profile.bio && (
                    <p className="text-white/90 text-sm leading-relaxed mb-4 max-w-xl">{profile.bio}</p>
                  )}
                </>
              )}

              <div className="flex items-center gap-6 mb-4 text-sm">
                <div><span className="text-white font-bold font-heading text-lg">{profile.posts_count}</span> <span className="text-[#A1A1AA]">mixes</span></div>
                <div><span className="text-white font-bold font-heading text-lg">{profile.followers_count}</span> <span className="text-[#A1A1AA]">followers</span></div>
                <div><span className="text-white font-bold font-heading text-lg">{profile.following_count}</span> <span className="text-[#A1A1AA]">following</span></div>
              </div>

              {isOwn && tips && (
                <div className="flex items-center gap-3 mb-4 p-3 rounded-xl border border-[#FFB000]/30 bg-[#FFB000]/5 max-w-md" data-testid="tips-received-tile">
                  <div className="w-10 h-10 rounded-full bg-[#FFB000]/20 flex items-center justify-center">
                    <HandCoins size={18} className="text-[#FFB000]" />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">Tips received</div>
                    <div className="text-white font-heading font-black text-xl">
                      £{((tips.total_pence || 0) / 100).toFixed(2)}
                      <span className="text-[#A1A1AA] text-xs font-mono font-normal ml-2">· {tips.count} tips</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2">
                {isOwn ? (
                  editing ? (
                    <>
                      <button onClick={handleSave} className="btn-pill btn-primary flex items-center gap-2" data-testid="profile-save">
                        <Check size={14} /> Save
                      </button>
                      <button onClick={() => { setEditing(false); setBio(profile.bio || ''); setName(profile.name || ''); }} className="btn-pill btn-outline flex items-center gap-2" data-testid="profile-cancel">
                        <X size={14} /> Cancel
                      </button>
                    </>
                  ) : (
                    <button onClick={() => setEditing(true)} className="btn-pill btn-outline flex items-center gap-2" data-testid="profile-edit">
                      <Edit3 size={14} /> Edit Profile
                    </button>
                  )
                ) : (
                  <>
                    <button
                      onClick={handleFollow}
                      className={`btn-pill flex items-center gap-2 ${profile.is_following ? 'btn-outline' : 'btn-primary'}`}
                      data-testid="profile-follow-btn"
                    >
                      {profile.is_following ? <UserCheck size={14} /> : <UserPlus size={14} />}
                      {profile.is_following ? 'Following' : 'Follow'}
                    </button>
                    <button
                      onClick={() => navigate(`/messages/${profile.user_id}`)}
                      className="btn-pill btn-outline flex items-center gap-2"
                      data-testid="profile-message-btn"
                    >
                      <MessageSquare size={14} /> Message
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/5">
          <button
            onClick={() => setTab('posts')}
            className={`px-5 py-3 text-sm font-bold font-heading transition-colors ${
              tab === 'posts' ? 'text-[#FFB000] border-b-2 border-[#FFB000]' : 'text-[#A1A1AA] hover:text-white'
            }`}
            data-testid="tab-posts"
          >
            Mixes ({posts.length})
          </button>
          {isOwn && (
            <button
              onClick={() => setTab('saved')}
              className={`px-5 py-3 text-sm font-bold font-heading transition-colors ${
                tab === 'saved' ? 'text-[#FFB000] border-b-2 border-[#FFB000]' : 'text-[#A1A1AA] hover:text-white'
              }`}
              data-testid="tab-saved"
            >
              Saved ({savedPosts.length})
            </button>
          )}
        </div>

        {/* Posts */}
        <div className="space-y-6">
          {tab === 'posts' ? (
            posts.length === 0 ? (
              <div className="text-center text-[#A1A1AA] py-12">No mixes yet.</div>
            ) : (
              posts.map(p => <PostCard key={p.id} post={p} onCommentOpen={setActiveComment} />)
            )
          ) : (
            savedPosts.length === 0 ? (
              <div className="text-center text-[#A1A1AA] py-12">You haven't saved any mixes yet.</div>
            ) : (
              savedPosts.map(p => <PostCard key={p.id} post={p} onCommentOpen={setActiveComment} />)
            )
          )}
        </div>
      </motion.div>
      <CommentDrawer post={activeComment} onClose={() => setActiveComment(null)} />
    </Layout>
  );
};

export default Profile;
